'use client';

import { useState, useEffect, useMemo, useRef } from 'react';
import { Modal } from '@shared/ui';
import { Select } from '@shared/ui/inputs/Select';
import type { BaseModalProps } from '@shared/types/modal.types';
import type { CategoryCard } from '@hummii/shared-types';
import { ordersApi, type CreateOrderRequest, type OrderType } from '@/src/shared/api';
import { OrderPhotoUploader } from '@/src/features/orders/components/OrderPhotoUploader';
import { toast } from 'sonner';
import { useAllCategories } from '@/src/entities/category/model/hooks/useAllCategories';
import { flattenCategoryTree } from '@/src/entities/category/api/fetchAllCategories';
import { fetchContractorCategories } from '@/src/entities/contractor/api';
import { AddressAutocomplete, type AddressAutocompleteResult } from './components/AddressAutocomplete';
import { useMyClientProfile } from '@/src/entities/user/model/hooks';
import { useArchiveWarningToast } from '@/src/shared/hooks';

type ActorType = 'CLIENT' | 'WORKER';

interface PostTaskModalProps extends BaseModalProps {
  categories?: CategoryCard[];
  onSuccess?: (taskId: string) => void;
  initialCategoryId?: string;
  // Props for DIRECT orders
  orderType?: OrderType;
  targetActorId?: string;
  targetActorName?: string;
  currentActorType?: ActorType;
}

/**
 * Post Task Modal - allows users to create a new task/order
 *
 * Supports two modes:
 * 1. PUBLIC order - full form with location (for job board)
 * 2. DIRECT order - simplified form (personal order between two parties)
 *
 * Usage:
 * - PUBLIC: const { open } = useModal('post-task'); open({ categories, onSuccess });
 * - DIRECT: open({ orderType: 'DIRECT', targetActorId, targetActorName, currentActorType });
 */
export function PostTaskModal({
  isOpen,
  onClose,
  categories = [],
  onSuccess,
  initialCategoryId,
  orderType = 'PUBLIC',
  targetActorId,
  targetActorName,
  currentActorType,
}: PostTaskModalProps) {
  // Debug logging
  console.log('[PostTaskModal] Props:', {
    orderType,
    targetActorId,
    targetActorName,
    currentActorType,
  });

  const isDirectOrder = orderType === 'DIRECT';

  const { showIfNeeded: showArchiveWarning } = useArchiveWarningToast();

  // Auto-load all categories from API (cached for 10 min)
  const { categoryTree, loading: categoriesLoading } = useAllCategories({
    enabled: isOpen, // Only fetch when modal is open
  });

  // Flatten category tree to get all categories as options
  const allCategories: CategoryCard[] = useMemo(() => {
    if (!categoryTree || categoryTree.length === 0) return [];
    return flattenCategoryTree(categoryTree).map((cat) => ({
      id: cat.id,
      nameEn: cat.nameEn,
      nameFr: cat.nameFr || cat.nameEn,
      slug: cat.slug,
      icon: cat.icon,
      iconUrl: cat.icon,
      imageUrl: cat.imageUrl ?? null,
      isPopular: cat.isPopular || false,
    }));
  }, [categoryTree]);

  // For DIRECT orders, load target worker's categories
  const [workerCategories, setWorkerCategories] = useState<CategoryCard[]>([]);
  const [loadingWorkerCategories, setLoadingWorkerCategories] = useState(false);

  useEffect(() => {
    if (isDirectOrder && targetActorId && isOpen) {
      setLoadingWorkerCategories(true);
      fetchContractorCategories(targetActorId)
        .then((cats) => {
          const mapped: CategoryCard[] = cats.map((c) => ({
            id: c.category.id,
            nameEn: c.category.nameEn,
            nameFr: c.category.nameFr,
            slug: c.category.slug,
            icon: c.category.icon,
            iconUrl: c.category.icon,
            imageUrl: null,
            isPopular: false,
          }));
          setWorkerCategories(mapped);

          // Pre-select first category if no initialCategoryId
          if (!initialCategoryId && mapped.length > 0) {
            setFormData((prev) => ({ ...prev, categoryId: mapped[0].id }));
          }
        })
        .catch((error) => {
          console.error('[PostTaskModal] Failed to load worker categories:', error);
          setWorkerCategories([]);
        })
        .finally(() => {
          setLoadingWorkerCategories(false);
        });
    }
  }, [isDirectOrder, targetActorId, isOpen, initialCategoryId]);

  // Determine which categories to display based on context
  // Priority: 1. Passed categories prop, 2. Worker categories (DIRECT), 3. All categories
  const displayCategories = useMemo(() => {
    if (categories.length > 0) return categories;
    if (isDirectOrder && workerCategories.length > 0) return workerCategories;
    return allCategories;
  }, [categories, isDirectOrder, workerCategories, allCategories]);

  const isCategoriesLoading = categoriesLoading || loadingWorkerCategories;

  const categoryOptions = useMemo(
    () => displayCategories.map((cat) => ({ value: cat.id, label: cat.nameEn })),
    [displayCategories]
  );

  // For DIRECT orders, use 2-step flow (skip location)
  type StepType = 'details' | 'location' | 'budget';
  const [step, setStep] = useState<StepType>('details');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const locationPrePopulated = useRef(false);

  const { data: clientProfile, isLoading: clientProfileLoading } = useMyClientProfile(
    isOpen && !isDirectOrder
  );

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    categoryId: initialCategoryId || '',
    budget: '',
    addressSearch: '',
    address: '',
    city: '',
    province: '',
    postalCode: '',
    startDate: '',
    latitude: undefined as number | undefined,
    longitude: undefined as number | undefined,
    images: [] as string[],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        title: '',
        description: '',
        categoryId: initialCategoryId || '',
        budget: '',
        addressSearch: '',
        address: '',
        city: '',
        province: '',
        postalCode: '',
        startDate: '',
        latitude: undefined,
        longitude: undefined,
        images: [],
      });
      locationPrePopulated.current = false;
      setStep('details');
      setErrors({});
    }
  }, [isOpen, initialCategoryId]);

  // Pre-populate location from client profile when entering location step
  useEffect(() => {
    if (
      step === 'location' &&
      !isDirectOrder &&
      !locationPrePopulated.current &&
      clientProfile?.latitude != null &&
      clientProfile?.longitude != null
    ) {
      locationPrePopulated.current = true;
      const profileCity = clientProfile.city || '';
      const profileProvince = clientProfile.province || '';
      setFormData((prev) => ({
        ...prev,
        latitude: clientProfile.latitude!,
        longitude: clientProfile.longitude!,
        address: prev.address || clientProfile.address || '',
        city: prev.city || profileCity,
        province: prev.province || profileProvince,
        postalCode: prev.postalCode || clientProfile.postalCode || '',
        addressSearch:
          prev.addressSearch ||
          [clientProfile.address, profileCity, profileProvince, clientProfile.postalCode]
            .filter(Boolean)
            .join(', '),
      }));
    }
  }, [step, clientProfile, isDirectOrder]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep = (currentStep: StepType): boolean => {
    const newErrors: Record<string, string> = {};

    if (currentStep === 'details') {
      if (!formData.title.trim()) {
        newErrors.title = 'Title is required';
      }

      if (!formData.description.trim()) {
        newErrors.description = 'Description is required';
      }

      // Category is optional for DIRECT orders
      if (!isDirectOrder && !formData.categoryId) {
        newErrors.categoryId = 'Please select a category';
      }
    }

    if (currentStep === 'budget') {
      if (formData.budget && parseFloat(formData.budget) > 99999999) {
        newErrors.budget = 'Budget cannot exceed $99,999,999 CAD';
      }
    }

    if (currentStep === 'location') {
      // Location is only required for PUBLIC orders
      if (!isDirectOrder) {
        if (formData.latitude == null || formData.longitude == null) {
          newErrors.coordinates = 'Please select an address from the suggestions';
        }
        if (!formData.address.trim()) {
          newErrors.address = 'Address is required';
        }
        if (!formData.city.trim()) {
          newErrors.city = 'City is required';
        }
        if (!formData.province) {
          newErrors.province = 'Province is required';
        }
        if (!formData.postalCode.trim()) {
          newErrors.postalCode = 'Postal code is required';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    console.log('[PostTaskModal] handleNext called, current step:', step);
    if (validateStep(step)) {
      if (step === 'details') {
        // Skip location step for DIRECT orders
        if (isDirectOrder) {
          console.log('[PostTaskModal] Moving from details to budget (DIRECT order)');
          setStep('budget');
        } else {
          console.log('[PostTaskModal] Moving from details to location (PUBLIC order)');
          setStep('location');
        }
      } else if (step === 'location') {
        console.log('[PostTaskModal] Moving from location to budget');
        setStep('budget');
      }
    } else {
      console.log('[PostTaskModal] Validation failed, staying on step:', step);
    }
  };

  const handleBack = () => {
    if (step === 'budget') {
      // Go back to details for DIRECT, location for PUBLIC
      setStep(isDirectOrder ? 'details' : 'location');
    } else if (step === 'location') {
      setStep('details');
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    console.log('[PostTaskModal] handleSubmit called, current step:', step);

    // Prevent submit if not on budget step (e.g., Enter key in input field)
    if (step !== 'budget') {
      console.log('[PostTaskModal] Not on budget step, calling handleNext instead');
      // If on details step, move to next step instead
      handleNext();
      return;
    }

    console.log('[PostTaskModal] On budget step, proceeding with order creation');
    if (!validateStep('budget')) return;

    setIsSubmitting(true);

    try {
      // Build order data
      const categoryId = formData.categoryId || displayCategories[0]?.id;
      const orderData: CreateOrderRequest = {
        title: formData.title,
        description: formData.description,
        type: orderType,
        categoryId: categoryId || undefined, // Don't send empty string
        budget: formData.budget ? parseFloat(formData.budget) : undefined,
        deadline: formData.startDate || undefined,
        images: formData.images.length > 0 ? formData.images : undefined,
      };

      // Add location for PUBLIC orders
      if (!isDirectOrder) {
        orderData.latitude = formData.latitude;
        orderData.longitude = formData.longitude;
        orderData.address = formData.address;
        orderData.city = formData.city;
        orderData.province = formData.province;
        orderData.postalCode = formData.postalCode;
      }

      // Add target actor for DIRECT orders
      if (isDirectOrder) {
        if (!targetActorId) {
          throw new Error('DIRECT orders require a target actor. Please open this modal from a chat conversation.');
        }
        if (currentActorType === 'CLIENT') {
          // Client creating order for specific worker
          orderData.workerActorId = targetActorId;
        } else {
          // Worker creating order for client
          orderData.clientActorId = targetActorId;
        }
      }

      // Create and publish order
      const order = await ordersApi.createAndPublish(orderData);

      console.log('Order created:', order);

      // Show success toast
      toast.success(isDirectOrder ? 'Order created successfully!' : 'Task posted successfully!');

      // Archive warning for public orders (shown if not suppressed by user)
      if (!isDirectOrder) {
        showArchiveWarning(); // fire-and-forget
      }

      // Call success callback
      onSuccess?.(order.id);

      // Close modal
      onClose();
    } catch (error) {
      console.error('Failed to create order:', error);
      const errorMessage = error instanceof Error ? error.message : '';
      let displayMessage = 'Failed to create order. Please try again.';

      if (errorMessage.includes('Cannot create an order for yourself')) {
        displayMessage = 'You cannot create an order for yourself. Please select a different contractor or switch account mode.';
      } else if (errorMessage) {
        displayMessage = errorMessage;
      }

      toast.error(displayMessage);
      setErrors({ submit: displayMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate step number for display
  const getStepNumber = () => {
    if (step === 'details') return 1;
    if (step === 'location') return 2;
    if (step === 'budget') return isDirectOrder ? 2 : 3;
    return 1;
  };

  const totalSteps = isDirectOrder ? 2 : 3;
  const modalTitle = isDirectOrder
    ? 'Create Direct Order'
    : 'Post a New Task';
  const modalDescription = isDirectOrder && targetActorName
    ? `Creating order for ${targetActorName}`
    : `Step ${getStepNumber()} of ${totalSteps}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      animation="slide-up"
      title={modalTitle}
      description={modalDescription}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Progress indicator */}
        <div className="flex items-center justify-between mb-6">
          {/* Step 1: Details */}
          <div className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step === 'details'
                  ? 'bg-blue-600 text-white'
                  : 'bg-green-500 text-white'
              }`}
            >
              1
            </div>
            <div className="text-sm text-gray-600">Details</div>
          </div>

          <div className="flex-1 h-1 bg-gray-200 mx-2">
            <div
              className={`h-full transition-all ${
                step !== 'details' ? 'bg-blue-600 w-full' : 'bg-gray-200 w-0'
              }`}
            />
          </div>

          {/* Step 2: Location (PUBLIC only) or Budget (DIRECT) */}
          {!isDirectOrder && (
            <>
              <div className="flex items-center gap-2">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step === 'location'
                      ? 'bg-blue-600 text-white'
                      : step === 'budget'
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-300 text-gray-600'
                  }`}
                >
                  2
                </div>
                <div className="text-sm text-gray-600">Location</div>
              </div>

              <div className="flex-1 h-1 bg-gray-200 mx-2">
                <div
                  className={`h-full transition-all ${
                    step === 'budget' ? 'bg-blue-600 w-full' : 'bg-gray-200 w-0'
                  }`}
                />
              </div>
            </>
          )}

          {/* Final Step: Budget */}
          <div className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step === 'budget'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-300 text-gray-600'
              }`}
            >
              {isDirectOrder ? 2 : 3}
            </div>
            <div className="text-sm text-gray-600">Budget</div>
          </div>
        </div>

        {/* Direct Order Banner */}
        {isDirectOrder && targetActorName && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-4">
            <p className="text-sm text-blue-800">
              This is a direct order for <strong>{targetActorName}</strong>.
              Location details can be discussed in chat.
            </p>
          </div>
        )}

        {/* Step 1: Details */}
        {step === 'details' && (
          <div className="space-y-4">
            {/* Title */}
            <div>
              <label
                htmlFor="title"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {isDirectOrder ? 'Order Title' : 'Task Title'} *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                  errors.title
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
                placeholder={
                  isDirectOrder
                    ? 'e.g., Home cleaning service'
                    : 'e.g., Fix leaking kitchen sink'
                }
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600">{errors.title}</p>
              )}
            </div>

            {/* Category - optional for DIRECT */}
            <div>
              <label
                htmlFor="categoryId"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Category {isDirectOrder ? '(optional)' : '*'}
              </label>
              <Select
                options={categoryOptions}
                value={formData.categoryId}
                onChange={(val) => setFormData((prev) => ({ ...prev, categoryId: val }))}
                placeholder={isCategoriesLoading ? 'Loading categories...' : 'Search category...'}
                disabled={isCategoriesLoading}
                searchable
                fullWidth
                error={!!errors.categoryId}
              />
              {isDirectOrder && workerCategories.length > 0 && (
                <p className="mt-1 text-xs text-gray-500">
                  Showing categories from {targetActorName}&apos;s profile
                </p>
              )}
              {errors.categoryId && (
                <p className="mt-1 text-sm text-red-600">{errors.categoryId}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Description *
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={5}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                  errors.description
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
                placeholder="Describe your task in detail..."
                maxLength={5000}
              />
              <p className="mt-1 text-xs text-gray-500">
                {formData.description.length}/5000 characters
              </p>
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">{errors.description}</p>
              )}
            </div>

            {/* Photos */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Photos (optional)
              </label>
              <OrderPhotoUploader
                images={formData.images}
                onChange={(imgs) => setFormData((prev) => ({ ...prev, images: imgs }))}
              />
            </div>
          </div>
        )}

        {/* Step 2: Location (PUBLIC only) */}
        {step === 'location' && !isDirectOrder && (
          <div className="space-y-4">
            {/* Profile location status banner */}
            {clientProfileLoading ? (
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-500">
                Loading your profile location...
              </div>
            ) : clientProfile?.latitude != null && clientProfile?.longitude != null ? (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
                <span className="text-green-600 flex-shrink-0 font-bold">✓</span>
                <div>
                  <p className="text-sm font-medium text-green-800">Location pre-filled from your profile</p>
                  <p className="text-xs text-green-600 mt-0.5">
                    {[clientProfile.city, clientProfile.province].filter(Boolean).join(', ')}
                    {' · '}You can update it by typing a new address below
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                <span className="text-amber-600 flex-shrink-0 font-bold">!</span>
                <div>
                  <p className="text-sm font-medium text-amber-800">No location found in your profile</p>
                  <p className="text-xs text-amber-700 mt-0.5">Start typing your address to search and select from suggestions</p>
                </div>
              </div>
            )}

            {/* Address autocomplete */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Task Location *
              </label>
              <AddressAutocomplete
                value={formData.addressSearch}
                onChange={(val) => setFormData((prev) => ({ ...prev, addressSearch: val }))}
                onSelect={(result: AddressAutocompleteResult) => {
                  setFormData((prev) => ({
                    ...prev,
                    addressSearch: result.formattedAddress,
                    address: result.address || prev.address,
                    city: result.city || prev.city,
                    province: result.province || prev.province,
                    postalCode: result.postalCode || prev.postalCode,
                    latitude: result.latitude,
                    longitude: result.longitude,
                  }));
                  if (errors.coordinates) {
                    setErrors((prev) => ({ ...prev, coordinates: '' }));
                  }
                }}
                placeholder="e.g. 123 Main St, Toronto, ON"
                error={errors.coordinates}
              />
              {errors.coordinates && (
                <p className="mt-1 text-sm text-red-600">{errors.coordinates}</p>
              )}
              {formData.latitude != null && formData.longitude != null && (
                <p className="mt-1 text-xs text-green-600">
                  ✓ Coordinates confirmed ({formData.latitude.toFixed(4)}, {formData.longitude.toFixed(4)})
                </p>
              )}
            </div>

            {/* Address detail fields (auto-filled, editable) */}
            <div className="space-y-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-xs text-gray-500 font-medium">Address details (auto-filled, edit if needed)</p>

              <div>
                <label htmlFor="address" className="block text-xs font-medium text-gray-600 mb-1">
                  Street Address *
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 ${
                    errors.address ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                  }`}
                  placeholder="123 Main Street"
                />
                {errors.address && <p className="mt-1 text-xs text-red-600">{errors.address}</p>}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="city" className="block text-xs font-medium text-gray-600 mb-1">
                    City *
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 ${
                      errors.city ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    placeholder="Toronto"
                  />
                  {errors.city && <p className="mt-1 text-xs text-red-600">{errors.city}</p>}
                </div>

                <div>
                  <label htmlFor="province" className="block text-xs font-medium text-gray-600 mb-1">
                    Province *
                  </label>
                  <select
                    id="province"
                    name="province"
                    value={formData.province}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 ${
                      errors.province ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                    }`}
                  >
                    <option value="">Select</option>
                    <option value="AB">Alberta</option>
                    <option value="BC">British Columbia</option>
                    <option value="MB">Manitoba</option>
                    <option value="NB">New Brunswick</option>
                    <option value="NL">Newfoundland and Labrador</option>
                    <option value="NS">Nova Scotia</option>
                    <option value="NT">Northwest Territories</option>
                    <option value="NU">Nunavut</option>
                    <option value="ON">Ontario</option>
                    <option value="PE">Prince Edward Island</option>
                    <option value="QC">Quebec</option>
                    <option value="SK">Saskatchewan</option>
                    <option value="YT">Yukon</option>
                  </select>
                  {errors.province && <p className="mt-1 text-xs text-red-600">{errors.province}</p>}
                </div>
              </div>

              <div>
                <label htmlFor="postalCode" className="block text-xs font-medium text-gray-600 mb-1">
                  Postal Code *
                </label>
                <input
                  type="text"
                  id="postalCode"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 ${
                    errors.postalCode ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                  }`}
                  placeholder="M5H 2N2"
                />
                {errors.postalCode && <p className="mt-1 text-xs text-red-600">{errors.postalCode}</p>}
              </div>
            </div>
          </div>
        )}

        {/* Final Step: Budget */}
        {step === 'budget' && (
          <div className="space-y-4">
            <div>
              <label
                htmlFor="budget"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Budget (Optional)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500">$</span>
                <input
                  type="number"
                  id="budget"
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  min="0"
                  max="99999999"
                  step="0.01"
                  className={`w-full pl-7 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.budget ? 'border-red-300' : 'border-gray-300'}`}
                  placeholder="0.00"
                />
              </div>
              {errors.budget && (
                <p className="mt-1 text-xs text-red-500">{errors.budget}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                {isDirectOrder
                  ? 'You can discuss the final price in chat'
                  : 'Leave blank if you want contractors to propose their prices'}
              </p>
            </div>

            <div>
              <label
                htmlFor="startDate"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Preferred Start Date (Optional)
              </label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {errors.submit && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{errors.submit}</p>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          {step !== 'details' && (
            <button
              type="button"
              onClick={handleBack}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Back
            </button>
          )}
          {step === 'details' && (
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          )}
          {step !== 'budget' ? (
            <button
              type="button"
              onClick={handleNext}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={() => handleSubmit()}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting
                ? 'Creating...'
                : isDirectOrder
                  ? 'Create Order'
                  : 'Post Task'}
            </button>
          )}
        </div>
      </form>
    </Modal>
  );
}
